import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Sun, Moon } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import GoogleLoginButton from '../components/GoogleLoginButton';

export default function Login() {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/discover');
    } catch (err) {
      setError(err.response?.data?.detail || t('common.error'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{background:'var(--bg-primary)'}}>
      <button onClick={toggleTheme} className="fixed top-5 right-5 p-3 rounded-full glass-card-hover" style={{color:'var(--text-muted)'}} data-testid="theme-toggle">
        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="logo.png" alt="AHOOW" className="h-16 w-16 rounded-2xl mx-auto mb-4" />
          <h1 className="text-3xl font-black mb-2" style={{color:'var(--text-primary)'}}>{t('auth.login')}</h1>
          <p style={{color:'var(--text-muted)'}}>{t('auth.loginSubtitle')}</p>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3 mb-6" data-testid="login-error">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2" style={{color:'var(--text-muted)'}}>{t('auth.email')}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" placeholder="votre@email.com" required data-testid="login-email" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{color:'var(--text-muted)'}}>{t('auth.password')}</label>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.password')} required data-testid="login-password" />
          </div>
          <button type="submit" disabled={loading} className="w-full py-4 rounded-full bg-gradient-to-r from-brand-pink via-rose-500 to-brand-violet text-white font-bold text-lg disabled:opacity-50 hover:shadow-lg hover:shadow-brand-pink/30 transition-all" data-testid="login-submit">
            {loading ? t('common.loading') : t('auth.login')}
          </button>
        </form>

        <GoogleLoginButton onSuccess={() => navigate('/discover')} onError={(msg) => setError(msg)} />

        <div className="mt-6 text-center space-y-3">
          <Link to="/forgot-password" className="text-sm text-brand-violet hover:opacity-80 transition-colors font-medium" data-testid="forgot-password-link">
            {t('auth.forgotPassword')}
          </Link>
          <p className="text-sm" style={{color:'var(--text-muted)'}}>
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-brand-pink font-semibold">{t('auth.register')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
